import { registerInitialState } from '@modules/authentication/constants';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import {
  LockRounded,
  MailRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToggle } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { AuthError } from 'v2/core/services/auth/errors';
import useResendOtp from 'v2/core/services/auth/useResendOtp';
import useSignIn from 'v2/core/services/auth/useSignIn';
import { RegisterState } from 'v2/views/layouts/authenticationModule/authTypes';

const trackForgotPasswordEvent = createAnalyticsTracker('forgotPassword');

interface SignInViewProps {
  setAuthErrorMessage: (errorMessage: string) => void;
  onSetUserPayload: (payload: RegisterState, otpView: boolean) => void;
  email: string;
  onChangeEmail: (email: string) => void;
}

export const SignInView = ({
  onSetUserPayload,
  setAuthErrorMessage,
  email,
  onChangeEmail,
}: SignInViewProps) => {
  const [showPassword, toggleShowPassword] = useToggle(false);
  const [password, setPassword] = useState('');

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const handleOnEmailChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChangeEmail(event.target.value);
  };

  const handleOnPasswordChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPassword(event.target.value);
  };

  const [resendOTP] = useResendOtp({
    email,
    onSuccess: () => {
      showSuccessNotification({
        contentComponent: 'Verification code has been sent succesfully.',
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        },
      });
    },
    onError: (err: AuthError) => {
      showErrorNotification({
        contentComponent: err.message,
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        },
      });
    },
  });

  const { signIn, loader, errorMessage } = useSignIn({
    email,
    password,
    onSuccess: () => {},
    onUserNotConfirmedError: () => {
      onSetUserPayload(
        { ...registerInitialState, email: email, password: password },
        true,
      );
      resendOTP();
    },
  });

  useEffect(() => {
    if (errorMessage) {
      setAuthErrorMessage(errorMessage);
    }
  }, [errorMessage, setAuthErrorMessage]);

  return (
    // Note (aryanshridhar): The backend headless browser relies on the type
    // field in this form (type='email', type='password', type='submit')
    // Connect with the BE before manipulating them to avoid any harm to
    // the headless browser's login process.
    <Box sx={{ width: '100%' }} component="form" onSubmit={signIn}>
      <Stack spacing={3}>
        <TextField
          label="Email Address"
          placeholder="Enter email address"
          onChange={handleOnEmailChange}
          type="email"
          value={email}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailRounded color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Password"
          placeholder="Enter your password"
          onChange={handleOnPasswordChange}
          type={showPassword ? 'text' : 'password'}
          value={password}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRounded color="disabled" />
              </InputAdornment>
            ),
            endAdornment: password?.length ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={toggleShowPassword}
                  onMouseDown={toggleShowPassword}
                >
                  {showPassword ? (
                    <VisibilityRounded />
                  ) : (
                    <VisibilityOffRounded />
                  )}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" justifyContent="end" mb={4}>
          <Box>
            <Link
              onClick={() =>
                trackForgotPasswordEvent({ eventName: 'Forgot Password' })
              }
              to="/forgot-password"
            >
              <Typography fontSize="12px" variant="subtitle1">
                Forgot password?
              </Typography>
            </Link>
          </Box>
        </Stack>
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          type="submit"
          loading={loader}
          disabled={loader}
          sx={{ mb: 2 }}
        >
          Sign In
        </LoadingButton>
      </Stack>
    </Box>
  );
};
